import { DefaultButton, Dialog, DialogFooter, IPalette, Stack } from '@fluentui/react';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { QueueConfiguration } from '../../../services';
import { CollapsableSection } from './CollapsableSection';
import { NavigationCard } from './NavigationCard';
import { NavigationState } from './NavigationState';
import { LocationDescriptorObject } from 'history';
import { getDisplayName } from 'components/util';
import { userIdsMatch } from '../../dashboard/card';
import { SalusPrimaryButton } from '../../shared/SalusPrimaryButton';

interface NavigationMap {
  [tenantId: string]: {
    displayName: string;
    titles: {
      [titleId: string]: {
        displayName: string;
        subjectTypes: [
          {
            name: string;
            config: QueueConfiguration;
          }
        ];
      };
    };
  };
}

type ITenantTitleSectionProps = RouteComponentProps & {
  configs: QueueConfiguration[];
  palette: IPalette;
  userId: string;
};

interface TenantTitleSectionState {
  showExitPrompt: boolean;
  navigationTarget: LocationDescriptorObject<{ passedQueueData: QueueConfiguration }> | undefined;
}

class TenantTitleSection extends React.PureComponent<ITenantTitleSectionProps, TenantTitleSectionState> {
  private navigationMap: NavigationMap;

  constructor(props) {
    super(props);

    this.navigationMap = this.props.configs
      .filter(config => config.allowedModerators.some(userRole => userIdsMatch(userRole.userId, this.props.userId)))
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .reduce(this.reduceConfigsToNavigationMap, {});
    this.state = {
      showExitPrompt: false,
      navigationTarget: undefined
    };
  }

  render(): JSX.Element {
    return (
      <Stack>
        {this.createNavigationView()}
        <Dialog
          hidden={!this.state.showExitPrompt}
          dialogContentProps={{
            title: 'Exit Review',
            subText:
              'Case has not been submitted. Leave the current case? If you click "Yes", the case will be placed back in the queue and you will continue to dashboard.'
          }}
          modalProps={{
            isBlocking: true,
            overlay: {
              isDarkThemed: true
            }
          }}
        >
          <DialogFooter>
            <SalusPrimaryButton onClick={(): void => this.closeDialog(true)} text="Yes" />
            <DefaultButton onClick={(): void => this.closeDialog(false)} text="No" />
          </DialogFooter>
        </Dialog>
      </Stack>
    );
  }

  private createNavigationView(): JSX.Element[] {
    const navigation: JSX.Element[] = [];
    Object.keys(this.navigationMap).forEach((key: string) => {
      const tenant = this.navigationMap[key];
      const titleNavigation: JSX.Element[] = [];

      Object.keys(tenant.titles).forEach((titleId: string) => {
        const title = tenant.titles[titleId];
        const queues: JSX.Element[] = [];
        title.subjectTypes.forEach(subjectType => {
          const isSelected: boolean = this.props.history.location.search.includes(subjectType.config.id);

          queues.push(
            <NavigationCard
              key={subjectType.config.id}
              displayName={subjectType.name}
              navigationState={isSelected ? NavigationState.REVIEW : NavigationState.NONE}
              config={subjectType.config}
              onNavigationClick={(target: NavigationState): void => {
                let navigationTarget: LocationDescriptorObject<{ passedQueueData: QueueConfiguration }> = {};

                if (target === NavigationState.REVIEW) {
                  navigationTarget = {
                    pathname: 'review',
                    search: `?id=${subjectType.config.id}`,
                    state: {
                      passedQueueData: subjectType.config
                    }
                  };
                } else if (target === NavigationState.SETTINGS) {
                  navigationTarget = {
                    pathname: 'settings',
                    search: `?id=${subjectType.config.id}`,
                    state: {
                      passedQueueData: subjectType.config
                    }
                  };
                }

                this.setState({
                  showExitPrompt:
                    this.props.history.location.pathname !== '/' &&
                    this.props.history.location.pathname !== '/settings',
                  navigationTarget
                });
              }}
            />
          );
        });

        titleNavigation.push(
          <CollapsableSection
            displayName={title.displayName}
            backgroundColor={this.props.palette.neutralQuaternaryAlt}
            className="py-2"
            variant="mediumPlus"
          >
            {queues}
          </CollapsableSection>
        );
      });

      navigation.push(
        <Stack.Item key={key}>
          <CollapsableSection
            displayName={tenant.displayName}
            backgroundColor={this.props.palette.neutralQuaternary}
            variant="large"
            className="py-2"
          >
            {titleNavigation}
          </CollapsableSection>
        </Stack.Item>
      );
    });

    return navigation;
  }

  private closeDialog(returnHome: boolean): void {
    this.setState({ showExitPrompt: false });
    if (returnHome && this.state.navigationTarget != null) {
      this.props.history.push(this.state.navigationTarget);
    }
  }

  private reduceConfigsToNavigationMap(acc: NavigationMap, current: QueueConfiguration): NavigationMap {
    const tenant = current.tenant;
    const title = current.title;
    const displayName = getDisplayName(current.subjectType, current.displayName);

    // New tenant and new title
    if (!acc[tenant.tenantId]) {
      acc[tenant.tenantId] = {
        displayName: tenant.displayName,
        titles: {
          [title.titleId]: {
            displayName: title.displayName,
            subjectTypes: [{ name: displayName, config: current }]
          }
        }
      };
      return acc;
    }

    // New title found
    if (!acc[tenant.tenantId].titles[title.titleId]) {
      acc[tenant.tenantId].titles[title.titleId] = {
        displayName: title.displayName,
        subjectTypes: [{ name: displayName, config: current }]
      };
      return acc;
    }

    // Add queue to existing map
    acc[tenant.tenantId].titles[title.titleId].subjectTypes.push({ name: displayName, config: current });
    return acc;
  }
}

export const TenantTitleSectionWithRouter = withRouter(TenantTitleSection);
